import type { GLTF } from 'three/examples/jsm/Addons.js'
import { GLTFLoader } from 'three/examples/jsm/Addons.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { withTimeout } from '@/lib/with-timeout'

type LoadState = 'pending' | 'resolved' | 'rejected'

type CacheEntry = {
  promise: Promise<GLTF>
  state: LoadState
  error: unknown
}

const loadedCache = new Map<string, CacheEntry>()

function withBasePath(url: string) {
  const prefix = process.env.NEXT_PUBLIC_BASE_PATH ?? ''
  return `${prefix}${url}`
}

export function preloadModel(url: string): Promise<GLTF> {
  const existing = loadedCache.get(url)
  if (existing) return existing.promise

  const loader = new GLTFLoader()
  loader.setMeshoptDecoder(MeshoptDecoder)
  const entry: CacheEntry = {
    state: 'pending',
    error: null,
    promise: Promise.resolve(null as unknown as GLTF),
  }

  const rawLoad = new Promise<GLTF>((resolve, reject) => {
      loader.load(
        withBasePath(url),
        resolve,
        undefined,
        (error) => reject(new Error(`GLTF load error for ${url}: ${String(error)}`)),
      )
    })

  entry.promise = withTimeout(rawLoad, 90000, `Model load timeout: ${url}`)
    .then((gltf) => {
      entry.state = 'resolved'
      return gltf
    })
    .catch((error) => {
      entry.state = 'rejected'
      entry.error = error
      // После таймаута или сетевой ошибки убираем promise из кеша:
      // повторный заход/refresh сможет попробовать скачать модель заново.
      loadedCache.delete(url)
      throw error
    })

  loadedCache.set(url, entry)
  return entry.promise
}

export function getLoadedModel(url: string): Promise<GLTF> {
  return preloadModel(url)
}

export function isModelLoaded(url: string): boolean {
  return loadedCache.get(url)?.state === 'resolved'
}

export function isModelSettled(url: string): boolean {
  const state = loadedCache.get(url)?.state
  return state === 'resolved' || state === 'rejected'
}

export function getModelLoadError(url: string): unknown {
  return loadedCache.get(url)?.error
}
